export type UnknownRecord = Record<string, unknown>;

export const isRecord = (value: unknown): value is UnknownRecord =>
    typeof value === "object" && value !== null && !Array.isArray(value);

export const getRecordValue = <T = unknown>(record: UnknownRecord, key: string): T | undefined =>
    record[key] as T | undefined;

/**
 * Unwraps common API payload shapes:
 * - { data: [...] }
 * - { data: { ... } }
 * - { data: { data: ... } }
 * - direct payload objects/arrays
 */
export const unwrapApiData = <T = unknown>(response: unknown): T | undefined => {
    if (Array.isArray(response)) {
        return response as T;
    }

    if (!isRecord(response)) {
        return undefined;
    }

    const topLevelData = getRecordValue(response, "data");
    if (topLevelData === undefined || topLevelData === null) {
        return response as T;
    }

    if (isRecord(topLevelData)) {
        const nestedData = getRecordValue(topLevelData, "data");
        if (nestedData !== undefined && nestedData !== null) {
            return nestedData as T;
        }
    }

    return topLevelData as T;
};

export const unwrapApiArray = <T = UnknownRecord>(response: unknown): T[] => {
    const payload = unwrapApiData<unknown>(response);
    return Array.isArray(payload) ? (payload as T[]) : [];
};

export const unwrapApiRecord = <T extends object = UnknownRecord>(response: unknown): T => {
    const payload = unwrapApiData<unknown>(response);
    return isRecord(payload) ? (payload as T) : ({} as T);
};

export const toNumber = (value: unknown, fallback = 0): number => {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === "string") {
        const parsed = Number(value.trim());
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return fallback;
};

export const toStringValue = (value: unknown, fallback = ""): string => {
    if (typeof value === "string") {
        return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    return fallback;
};

export const coerceNumericSummary = <T extends object>(
    summary: unknown,
    keys: readonly (keyof T & string)[]
): T | null => {
    if (!isRecord(summary)) {
        return null;
    }

    const result = {} as T;
    keys.forEach((key) => {
        (result as Record<string, unknown>)[key] = toNumber(summary[key]);
    });

    return result;
};
